"use client";

import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode, type RefObject } from "react";
import { submitAreaEvent, type AreaEventSubmitResult } from "@/app/actions/area-events";
import type { AreaEventFieldErrors, AreaEventFormField } from "@/lib/areas/event-submission";
import { createClient } from "@/lib/supabase";
import { AreaChevron } from "./area-a-parts";
import { intlTimeZoneAt } from "@/lib/time-zone-rules";

/**
 * "Add an event" (client island in the What's on card). The button opens an inline form; the
 * sign-in check runs on the client when it opens (the area page stays static), and the server
 * action checks again. Signed out: a sign-in link back to this section. Submissions go to the
 * moderated queue, so the success message says it shows once approved.
 */

export type AreaAddEventProps = Readonly<{
  areaSlug: string;
  areaName: string;
  pageSport: "surf" | "piste";
  timezone: string;
  /** The area's members, the location anchor choices. */
  locations: readonly Readonly<{ slug: string; name: string }>[];
  categories: readonly Readonly<{ value: string; label: string }>[];
  defaultCategory: string;
  /** "/login?redirect=..." back to this section. */
  signInHref: string;
  /** "/signup?next=..." back to this section, for visitors without an account. */
  signUpHref?: string;
  /** The id of the form panel (aria-controls). */
  formId: string;
}>;

type Session = "unknown" | "checking" | "signed-in" | "signed-out";

function todayIn(timezone: string): string {
  try {
    const now = new Date();
    const parts = new Intl.DateTimeFormat("en-CA", { timeZone: intlTimeZoneAt(timezone, now.getTime()), year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
    const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
    return `${get("year")}-${get("month")}-${get("day")}`;
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

const INPUT =
  "min-h-11 w-full rounded-[10px] border border-[#848E86] bg-white px-3 text-[15px] text-navy placeholder:text-[#7A8290] hover:border-navy aria-[invalid=true]:border-[#B42318]";

function Field({ id, label, optional = false, error, children }: Readonly<{ id: string; label: string; optional?: boolean; error?: string; children: ReactNode }>) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className="text-[13px] font-bold text-navy">
        {label}
        {optional ? <span className="font-normal text-[#566072]"> (optional)</span> : null}
      </label>
      {children}
      {error ? <p id={`${id}-error`} className="text-[12.5px] font-semibold text-[#B42318]">{error}</p> : null}
    </div>
  );
}

function SelectWrap({ children }: Readonly<{ children: ReactNode }>) {
  return (
    <span className="relative flex items-center">
      {children}
      <span aria-hidden="true" className="pointer-events-none absolute right-[11px] flex text-teal-dark">
        <AreaChevron />
      </span>
    </span>
  );
}

/** The form's fields in reading order: focus goes to the first one with an error. */
export const AREA_EVENT_FIELD_ORDER: readonly AreaEventFormField[] = [
  "name", "venue", "locationSlug", "date", "startTime", "endTime", "category", "link", "description",
];

export function firstErrorField(errors: AreaEventFieldErrors): AreaEventFormField | null {
  return AREA_EVENT_FIELD_ORDER.find((field) => Boolean(errors[field])) ?? null;
}

/** Signed out: what the form is for, a sign-in link and a sign-up link back to this section. */
export function AreaAddEventSignIn({ areaName, signInHref, signUpHref }: Readonly<{ areaName: string; signInHref: string; signUpHref?: string }>) {
  return (
    <div className="rounded-[14px] border border-line bg-[#F7F8F5] p-3.5">
      <p className="text-[14px] leading-[1.5] text-navy">Sign in to add an event for {areaName}. It is free, and we check every event before it appears.</p>
      <a
        href={signInHref}
        className="mt-2.5 inline-flex min-h-11 items-center rounded-[8px] bg-teal px-4 text-[14.5px] font-bold text-white no-underline hover:bg-teal-dark"
      >
        Sign in to add an event
      </a>
      {signUpHref ? (
        <p className="mt-2 text-[13.5px] text-[#566072]">
          New to PeakWave?{" "}
          <a href={signUpHref} className="inline-flex min-h-11 items-center font-bold text-teal-dark underline underline-offset-2 hover:text-teal">
            Create a free account
          </a>
        </p>
      ) : null}
    </div>
  );
}

export type AreaAddEventFormProps = Readonly<{
  formRef?: RefObject<HTMLFormElement | null>;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  idPrefix: string;
  areaName: string;
  locations: AreaAddEventProps["locations"];
  categories: AreaAddEventProps["categories"];
  defaultCategory: string;
  /** The area's local date, the earliest day. */
  today: string;
  errors: AreaEventFieldErrors;
  message: string | null;
  pending: boolean;
}>;

/** The form fields (signed in). */
export function AreaAddEventForm({ formRef, onSubmit, idPrefix, areaName, locations, categories, defaultCategory, today, errors, message, pending }: AreaAddEventFormProps) {
  const id = (field: AreaEventFormField) => `${idPrefix}-${field}`;
  const describedBy = (field: AreaEventFormField) => (errors[field] ? `${id(field)}-error` : undefined);
  return (
    <form ref={formRef} onSubmit={onSubmit} noValidate aria-label={`Add an event in ${areaName}`} className="flex flex-col gap-3">
      <Field id={id("name")} label="Event name" error={errors.name}>
        <input id={id("name")} name="name" type="text" required maxLength={160} autoComplete="off" aria-invalid={Boolean(errors.name)} aria-describedby={describedBy("name")} className={INPUT} />
      </Field>
      <Field id={id("venue")} label="Venue or meeting point" error={errors.venue}>
        <input id={id("venue")} name="venue" type="text" required maxLength={200} autoComplete="off" aria-invalid={Boolean(errors.venue)} aria-describedby={describedBy("venue")} className={INPUT} />
      </Field>
      <Field id={id("locationSlug")} label="Nearest place" error={errors.locationSlug}>
        <SelectWrap>
          <select id={id("locationSlug")} name="locationSlug" required defaultValue="" aria-invalid={Boolean(errors.locationSlug)} aria-describedby={describedBy("locationSlug")} className={`${INPUT} cursor-pointer appearance-none pr-[34px]`}>
            <option value="" disabled>Choose a place in {areaName}</option>
            {locations.map((location) => <option key={location.slug} value={location.slug}>{location.name}</option>)}
          </select>
        </SelectWrap>
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:items-end">
        <Field id={id("date")} label="Day" error={errors.date}>
          <input id={id("date")} name="date" type="date" required min={today || undefined} defaultValue={today} aria-invalid={Boolean(errors.date)} aria-describedby={describedBy("date")} className={INPUT} />
        </Field>
        <Field id={id("startTime")} label="Starts" error={errors.startTime}>
          <input id={id("startTime")} name="startTime" type="time" required aria-invalid={Boolean(errors.startTime)} aria-describedby={describedBy("startTime")} className={INPUT} />
        </Field>
        <Field id={id("endTime")} label="Finishes" optional error={errors.endTime}>
          <input id={id("endTime")} name="endTime" type="time" aria-invalid={Boolean(errors.endTime)} aria-describedby={describedBy("endTime")} className={INPUT} />
        </Field>
      </div>
      <p className="-mt-1 text-[12.5px] text-[#566072]">Times are local to {areaName}.</p>
      <Field id={id("category")} label="Category" error={errors.category}>
        <SelectWrap>
          <select id={id("category")} name="category" defaultValue={defaultCategory} aria-invalid={Boolean(errors.category)} aria-describedby={describedBy("category")} className={`${INPUT} cursor-pointer appearance-none pr-[34px]`}>
            {categories.map((category) => <option key={category.value} value={category.value}>{category.label}</option>)}
          </select>
        </SelectWrap>
      </Field>
      <Field id={id("link")} label="Link" optional error={errors.link}>
        <input id={id("link")} name="link" type="text" inputMode="url" maxLength={2048} placeholder="https://" autoComplete="off" aria-invalid={Boolean(errors.link)} aria-describedby={describedBy("link")} className={INPUT} />
      </Field>
      <Field id={id("description")} label="Short description" optional error={errors.description}>
        <textarea id={id("description")} name="description" rows={3} maxLength={500} aria-invalid={Boolean(errors.description)} aria-describedby={describedBy("description")} className={`${INPUT} py-2`} />
      </Field>
      {message ? <p role="alert" className="text-[13.5px] font-semibold text-[#B42318]">{message}</p> : null}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="submit"
          disabled={pending}
          className="inline-flex min-h-11 items-center rounded-[8px] bg-teal px-4 text-[14.5px] font-bold text-white hover:bg-teal-dark disabled:opacity-60"
        >
          {pending ? "Sending" : "Send for review"}
        </button>
        <p className="text-[12.5px] leading-[1.45] text-[#566072]">We check every event before it appears.</p>
      </div>
    </form>
  );
}

export function AreaAddEvent(props: AreaAddEventProps) {
  const { areaSlug, areaName, pageSport, timezone, locations, categories, defaultCategory, signInHref, signUpHref, formId } = props;
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState<Session>("unknown");
  const [pending, setPending] = useState(false);
  const [errors, setErrors] = useState<AreaEventFieldErrors>({});
  const [message, setMessage] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [today, setToday] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const uid = useId();
  // Bumped on each failed submit, so focus moves even when the same field fails twice.
  const [errorTick, setErrorTick] = useState(0);
  useEffect(() => {
    if (errorTick === 0) return;
    const field = firstErrorField(errors);
    // WCAG 3.3.1 and 2.4.3: take the person to the first invalid field (its error is linked
    // through aria-describedby); with no field error, the alert message is announced instead.
    if (field) document.getElementById(`${uid}-${field}`)?.focus();
  }, [errorTick, errors, uid]);
  const checkSession = async () => {
    setSession("checking");
    try {
      const { data } = await createClient().auth.getSession();
      setSession(data.session ? "signed-in" : "signed-out");
    } catch {
      // The server action checks again; let the person try.
      setSession("signed-in");
    }
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setToday(todayIn(timezone));
      if (session === "unknown") void checkSession();
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending) return;
    const data = new FormData(event.currentTarget);
    const value = (name: string) => {
      const entry = data.get(name);
      return typeof entry === "string" ? entry : "";
    };
    setPending(true);
    setMessage(null);
    let result: AreaEventSubmitResult;
    try {
      result = await submitAreaEvent({
        areaSlug,
        pageSport,
        name: value("name"),
        venue: value("venue"),
        locationSlug: value("locationSlug"),
        date: value("date"),
        startTime: value("startTime"),
        endTime: value("endTime"),
        link: value("link"),
        description: value("description"),
        category: value("category"),
      });
    } catch {
      result = { ok: false, code: "unavailable", error: "Adding events is not available right now. Please try again later." };
    }
    setPending(false);
    if (result.ok) {
      setErrors({});
      setDone(true);
      formRef.current?.reset();
      return;
    }
    if (result.code === "signed-out") setSession("signed-out");
    setErrors(result.fieldErrors ?? {});
    setMessage(result.error);
    setErrorTick((tick) => tick + 1);
  };

  return (
    <div className="mt-3">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={formId}
        onClick={toggle}
        data-area-add-event=""
        className="flex min-h-12 w-full items-center justify-between gap-2.5 rounded-[12px] border border-[#BFDDE1] bg-[#F2F8F8] px-3.5 text-left text-[14.5px] font-bold text-teal-dark transition-colors hover:border-teal hover:text-teal md:px-4 md:text-[15px]"
      >
        Add an event
        <span aria-hidden="true" className={`flex transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`}>
          <AreaChevron />
        </span>
      </button>

      <div id={formId} hidden={!open} className="mt-3">
        {session === "checking" || session === "unknown" ? (
          <p className="text-[13.5px] text-[#566072]" role="status">Checking you are signed in.</p>
        ) : session === "signed-out" ? (
          <AreaAddEventSignIn areaName={areaName} signInHref={signInHref} signUpHref={signUpHref} />
        ) : done ? (
          <div className="rounded-[14px] border border-[#BFDDE1] bg-[#F2F8F8] p-3.5" role="status">
            <p className="text-[14.5px] font-bold text-navy">Thanks, your event is in for review.</p>
            <p className="mt-1 text-[13.5px] leading-[1.5] text-[#566072]">It shows here once we have checked it.</p>
            <button
              type="button"
              onClick={() => setDone(false)}
              className="mt-2.5 inline-flex min-h-11 items-center rounded-[8px] border border-[#848E86] bg-white px-3.5 text-[14px] font-bold text-navy hover:border-navy"
            >
              Add another
            </button>
          </div>
        ) : (
          <AreaAddEventForm
            formRef={formRef}
            onSubmit={onSubmit}
            idPrefix={uid}
            areaName={areaName}
            locations={locations}
            categories={categories}
            defaultCategory={defaultCategory}
            today={today}
            errors={errors}
            message={message}
            pending={pending}
          />
        )}
      </div>
    </div>
  );
}
